import { useCallback, useEffect, useRef, useState } from "react";
import {
  ICancellationPreview,
  ICancellationRequest
} from "../../../interfaces/reservations/Cancellation.types";
import { previewCancellation } from "../../../services/Reservations.service";
import { resolveError } from "../../../utils/apiErrorMessage";
import { previewKey } from "../../../utils/reservations/cancellation";

interface PreviewState {
  preview: ICancellationPreview | null;
  loading: boolean;
  error: string | null;
}

// Re-asks the server whenever the selection changes; only the newest answer is kept, and the
// previous one stays on screen (dimmed by the caller) until it arrives.
const useCancellationPreview = (request: ICancellationRequest) => {
  const [state, setState] = useState<PreviewState>({ preview: null, loading: true, error: null });
  const latest = useRef(request);
  latest.current = request;
  const sequence = useRef(0);

  const load = useCallback(() => {
    const id = ++sequence.current;
    const current = latest.current;
    if (current.ticketIds.length === 0) {
      setState({ preview: null, loading: false, error: null });
      return;
    }
    setState((prev) => ({ ...prev, loading: true, error: null }));
    previewCancellation(current)
      .then((res) => {
        if (id === sequence.current)
          setState({ preview: res.data ?? null, loading: false, error: null });
      })
      .catch((err) => {
        if (id === sequence.current)
          setState({
            preview: null,
            loading: false,
            error: resolveError(err, "Shumat e rimbursimit nuk u ngarkuan.")
          });
      });
  }, []);

  const key = previewKey(request);
  useEffect(() => {
    // Loading from the first keystroke, not from the request: the figures on screen no longer
    // match the selection, so nothing may be submitted against them.
    setState((prev) => (prev.loading ? prev : { ...prev, loading: true }));
    const timer = setTimeout(load, 250);
    return () => clearTimeout(timer);
  }, [key, load]);

  return { ...state, reload: load };
};

export default useCancellationPreview;
